import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  OpenInNew as OpenIcon,
  PlaylistAddCheck as InsertIcon,
  Search as SearchIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { saveAs } from 'file-saver';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import {
  CITATION_STYLES, DEFAULT_CITATION_STYLE, citedCount, isQuickStyle, extractDois, formatAll, googleScholarUrl,
  parseReferences, parseReferenceFile, referenceKey, referencesFromDois, searchCrossref, toBibtex,
} from './citations';

/**
 * Bibliography manager for a publication.
 * references: stored list [{ id, csl?, doi, text, cited? }]; onChange(list) persists it.
 * Cited references sit first, in citation order, so their position is their in-text number.
 * onRebuildReferences() rewrites the References section from the cited references.
 * style / styleTitle is the publication's citation style; onStyleChange(style) saves it.
 */
const ReferencesPanel = ({
  references = [], canEdit, onChange, onRebuildReferences, style = DEFAULT_CITATION_STYLE, styleTitle, onStyleChange,
}) => {
  const { showSnackbar } = useSnackbar();
  const [formatted, setFormatted] = useState([]);
  const [mode, setMode] = useState('bibtex');
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    formatAll(references, style)
      .then((list) => { if (!cancelled) setFormatted(list); })
      .catch(() => { if (!cancelled) setFormatted(references.map((r) => r.text || r.csl?.title || '')); });
    return () => { cancelled = true; };
  }, [references, style]);

  const existingKeys = useMemo(() => new Set(references.map(referenceKey)), [references]);

  const addReferences = async (incoming, { suffix = '' } = {}) => {
    // Skip references already in the bibliography, and repeats within this batch
    const seen = new Set(existingKeys);
    const fresh = incoming.filter((r) => {
      const key = referenceKey(r);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const skipped = incoming.length - fresh.length;
    if (fresh.length) await onChange([...references, ...fresh]);
    showSnackbar(
      `${fresh.length} reference${fresh.length === 1 ? '' : 's'} added${skipped ? `, ${skipped} duplicate${skipped === 1 ? '' : 's'} skipped` : ''}${suffix}`,
      fresh.length ? 'success' : 'info',
    );
    return fresh.length;
  };

  const run = async (fn, failure) => {
    setBusy(true);
    try { await fn(); } catch (err) {
      console.error(failure, err);
      showSnackbar(failure, 'error');
    } finally { setBusy(false); }
  };

  const importText = () => run(async () => {
    const parsed = await parseReferences(input);
    if (await addReferences(parsed) >= 0) setInput('');
  }, 'Could not read those references. Paste BibTeX, RIS or CSL-JSON.');

  const importDois = () => run(async () => {
    const dois = extractDois(input);
    if (!dois.length) { showSnackbar('No DOIs found in the text', 'warning'); return; }
    await addReferences(await referencesFromDois(dois));
    setInput('');
  }, 'DOI lookup failed. Check the DOIs and your connection.');

  const addManual = () => {
    if (!input.trim()) return;
    addReferences([{ id: `r${Date.now().toString(36)}`, text: input.trim(), doi: extractDois(input)[0] || '' }]);
    setInput('');
  };

  const search = () => run(async () => { setHits(await searchCrossref(input)); }, 'Crossref search failed');

  // One or more library files (EndNote, BibTeX, RIS, CSL-JSON), added in a single update
  const importFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    run(async () => {
      const parsed = [];
      const failed = [];
      for (const file of files) {
        try {
          parsed.push(...await parseReferenceFile(file.name, await file.text()));
        } catch (err) {
          console.error(`Could not read ${file.name}`, err);
          failed.push(file.name);
        }
      }
      if (!parsed.length && failed.length) throw new Error('nothing readable');
      const from = files.length > 1 ? ` from ${files.length - failed.length} file${files.length - failed.length === 1 ? '' : 's'}` : '';
      await addReferences(parsed, { suffix: `${from}${failed.length ? `. Could not read: ${failed.join(', ')}` : ''}` });
    }, `Could not read ${files.map((f) => f.name).join(', ')}`);
  };

  const nCited = citedCount(references);

  const remove = (ref, i) => {
    if (ref.cited) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        `This reference is cited in the text as [${i + 1}]. Removing it shifts every later citation number down by one, `
        + 'so those in-text citations will need correcting. Remove it anyway?'
      );
      if (!ok) return;
    }
    onChange(references.filter((r) => r.id !== ref.id));
  };

  const move = (i, delta) => {
    const next = [...references];
    const [item] = next.splice(i, 1);
    next.splice(i + delta, 0, item);
    onChange(next);
  };

  const exportBib = () => run(async () => {
    saveAs(new Blob([await toBibtex(references)], { type: 'application/x-bibtex;charset=utf-8' }), 'references.bib');
  }, 'Export failed');

  return (
    <Stack spacing={2}>
      {canEdit && (
        <Paper sx={{ p: 2 }}>
          <Tabs value={mode} onChange={(e, v) => { setMode(v); setHits([]); }} sx={{ mb: 2 }} variant="scrollable">
            <Tab value="bibtex" label="BibTeX / RIS" />
            <Tab value="doi" label="DOI" />
            <Tab value="search" label="Search" />
            <Tab value="manual" label="Manual" />
          </Tabs>

          {mode === 'bibtex' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              From <strong>Google Scholar</strong>: click <em>Cite</em> under a result, choose <em>BibTeX</em>, and paste it here.
              For many papers, save them to <em>My library</em> and export as BibTeX. Exports from Zotero, Mendeley or EndNote (.bib / .ris) also work.
            </Typography>
          )}
          {mode === 'doi' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Paste one or more DOIs (or any text that contains them); metadata is fetched from doi.org.
            </Typography>
          )}
          {mode === 'search' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Searches the open Crossref index. Google Scholar has no public API, so use the link to search there, then paste its BibTeX.
            </Typography>
          )}

          <TextField
            fullWidth
            multiline={mode !== 'search'}
            minRows={mode === 'bibtex' ? 5 : 2}
            size="small"
            placeholder={{
              bibtex: '@article{lecun2015deep,\n  title={Deep learning}, ...\n}',
              doi: '10.1038/nature14539',
              search: 'Title, authors or keywords',
              manual: 'Formatted citation, e.g. Doe J, Roe R. Title. Journal. 2024;1(2):3–4.',
            }[mode]}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (mode === 'search' && e.key === 'Enter' && input.trim()) search(); }}
            sx={{ fontFamily: mode === 'bibtex' ? 'monospace' : undefined }}
          />

          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            {mode === 'bibtex' && (
              <>
                <Button variant="contained" startIcon={busy ? <CircularProgress size={16} /> : <AddIcon />} disabled={busy || !input.trim()} onClick={importText}>
                  Import
                </Button>
                <Tooltip title="Select one or more files: EndNote (.xml, .enw), BibTeX (.bib), RIS (.ris) or CSL-JSON. Zotero and Mendeley export these too.">
                  <Button startIcon={<UploadIcon />} onClick={() => fileRef.current?.click()} disabled={busy}>Upload libraries</Button>
                </Tooltip>
                <input ref={fileRef} type="file" multiple accept=".bib,.bibtex,.ris,.txt,.json,.xml,.enw" hidden onChange={importFiles} />
              </>
            )}
            {mode === 'doi' && (
              <Button variant="contained" startIcon={busy ? <CircularProgress size={16} /> : <AddIcon />} disabled={busy || !input.trim()} onClick={importDois}>
                Look up & add
              </Button>
            )}
            {mode === 'search' && (
              <>
                <Button variant="contained" startIcon={busy ? <CircularProgress size={16} /> : <SearchIcon />} disabled={busy || !input.trim()} onClick={search}>
                  Search Crossref
                </Button>
                <Button startIcon={<OpenIcon />} disabled={!input.trim()} href={googleScholarUrl(input)} target="_blank" rel="noopener noreferrer">
                  Open in Google Scholar
                </Button>
              </>
            )}
            {mode === 'manual' && (
              <Button variant="contained" startIcon={<AddIcon />} disabled={!input.trim()} onClick={addManual}>Add</Button>
            )}
          </Stack>

          {mode === 'search' && hits.length > 0 && (
            <List dense sx={{ mt: 1 }}>
              {hits.map((hit) => {
                const added = existingKeys.has(`doi:${hit.doi.toLowerCase()}`);
                return (
                  <ListItem
                    key={hit.doi}
                    divider
                    secondaryAction={(
                      <Button size="small" disabled={added || busy}
                        onClick={() => run(async () => { await addReferences(await referencesFromDois([hit.doi])); }, 'DOI lookup failed')}>
                        {added ? 'Added' : 'Add'}
                      </Button>
                    )}
                  >
                    <ListItemText
                      primary={hit.title}
                      secondary={[hit.authors, hit.venue, hit.year, hit.doi].filter(Boolean).join(' · ')}
                      sx={{ pr: 8 }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Bibliography ({references.length})
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>{nCited} cited</Typography>
          </Typography>
          <TextField select size="small" label="Style" value={style} onChange={(e) => onStyleChange?.(e.target.value)}
            disabled={!canEdit || !onStyleChange} sx={{ minWidth: 200 }}>
            {!isQuickStyle(style) && <MenuItem value={style}>{styleTitle || style}</MenuItem>}
            {CITATION_STYLES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <Button startIcon={<DownloadIcon />} onClick={exportBib} disabled={!references.length}>Export .bib</Button>
          {canEdit && onRebuildReferences && (
            <Tooltip title="The References section is updated automatically when you cite or change the style.">
              <span>
                <Button variant="contained" startIcon={<InsertIcon />} disabled={!nCited} onClick={() => onRebuildReferences()}>
                  Rebuild References section
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>

        {references.length === 0 ? (
          <Alert severity="info">No references yet. Import them above.</Alert>
        ) : references.map((ref, i) => (
          <Box key={ref.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Tooltip title={ref.cited ? `Cited in the text as [${i + 1}]` : 'Not cited yet'}>
              <Typography sx={{ minWidth: 36, fontWeight: 600, color: ref.cited ? 'text.primary' : 'text.disabled' }}>
                {ref.cited ? `[${i + 1}]` : '–'}
              </Typography>
            </Tooltip>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{formatted[i] || ref.text || ref.csl?.title}</Typography>
              {ref.doi && (
                <Link variant="caption" href={`https://doi.org/${ref.doi}`} target="_blank" rel="noopener noreferrer">doi:{ref.doi}</Link>
              )}
            </Box>
            {ref.cited && (
              <Tooltip title={`Copy in-text citation [${i + 1}]`}>
                <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(`[${i + 1}]`); showSnackbar(`Copied [${i + 1}]`, 'info'); }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canEdit && (
              <>
                {/* Cited references keep their numbers, so only uncited ones can be reordered (among themselves) */}
                <IconButton size="small" disabled={ref.cited || i <= nCited} onClick={() => move(i, -1)}><UpIcon fontSize="small" /></IconButton>
                <IconButton size="small" disabled={ref.cited || i === references.length - 1} onClick={() => move(i, 1)}><DownIcon fontSize="small" /></IconButton>
                <IconButton size="small" color="error" onClick={() => remove(ref, i)}><DeleteIcon fontSize="small" /></IconButton>
              </>
            )}
          </Box>
        ))}
        {references.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Cite references from the section editor's “Insert reference” button: they get the next number and are added
            to the References section automatically. Formatting by citation.js.
          </Typography>
        )}
      </Paper>
    </Stack>
  );
};

export default ReferencesPanel;
